import React from 'react';
import classNames from 'classnames';
import { arrayOf, bool, object, string } from 'prop-types';

import { FormattedMessage } from '../../util/reactIntl';
import {
  getStripeAccountData,
  getStripeMissingRequirementTypes,
  shouldShowMissingStripeInfoBanner,
  STRIPE_MISSING_REQUIREMENT_TYPES,
} from '../../util/stripeAccountStatus';

import css from './MissingStripeInfoBanner.module.css';

const REQUIREMENT_LABELS = {
  [STRIPE_MISSING_REQUIREMENT_TYPES.ACCOUNT_INFORMATION]: {
    id: 'MissingStripeInfoBanner.requirement.accountInformation',
    defaultMessage: 'Account information',
  },
  [STRIPE_MISSING_REQUIREMENT_TYPES.ADDRESS]: {
    id: 'MissingStripeInfoBanner.requirement.address',
    defaultMessage: 'Address',
  },
  [STRIPE_MISSING_REQUIREMENT_TYPES.BUSINESS_DETAILS]: {
    id: 'MissingStripeInfoBanner.requirement.businessDetails',
    defaultMessage: 'Business details',
  },
  [STRIPE_MISSING_REQUIREMENT_TYPES.EXTERNAL_ACCOUNT]: {
    id: 'MissingStripeInfoBanner.requirement.externalAccount',
    defaultMessage: 'Bank account details',
  },
  [STRIPE_MISSING_REQUIREMENT_TYPES.IDENTITY_VERIFICATION]: {
    id: 'MissingStripeInfoBanner.requirement.identityVerification',
    defaultMessage: 'Identity verification',
  },
  [STRIPE_MISSING_REQUIREMENT_TYPES.PAYOUT_CAPABILITY]: {
    id: 'MissingStripeInfoBanner.requirement.payoutCapability',
    defaultMessage: 'Payout capability',
  },
  [STRIPE_MISSING_REQUIREMENT_TYPES.TAX_INFORMATION]: {
    id: 'MissingStripeInfoBanner.requirement.taxInformation',
    defaultMessage: 'Tax information',
  },
  [STRIPE_MISSING_REQUIREMENT_TYPES.TERMS_ACCEPTANCE]: {
    id: 'MissingStripeInfoBanner.requirement.termsAcceptance',
    defaultMessage: 'Terms acceptance',
  },
};

const MissingRequirements = ({ requirementTypes }) => {
  const uniqueRequirementTypes = [...new Set(requirementTypes || [])].filter(Boolean);

  return uniqueRequirementTypes.length > 0 ? (
    <ul className={css.requirements}>
      {uniqueRequirementTypes.map(requirementType => {
        const label = REQUIREMENT_LABELS[requirementType];
        return label ? (
          <li key={requirementType}>
            <FormattedMessage {...label} />
          </li>
        ) : null;
      })}
    </ul>
  ) : null;
};

const MissingStripeInfoBanner = props => {
  const {
    rootClassName = null,
    className = null,
    currentUser = null,
    stripeAccount = null,
    stripeAccountData = null,
    stripeAccountFetched = false,
    stripeAccountFetchInProgress = false,
    sellerContext = false,
    stripeRequired = true,
  } = props;

  const shouldShow = shouldShowMissingStripeInfoBanner({
    currentUser,
    stripeAccount,
    stripeAccountData,
    stripeAccountFetched,
    stripeAccountFetchInProgress,
    sellerContext,
    stripeRequired,
  });

  if (!shouldShow) {
    return null;
  }

  const resolvedStripeAccountData =
    stripeAccountData ||
    getStripeAccountData(stripeAccount) ||
    getStripeAccountData(currentUser?.stripeAccount);
  const missingRequirementTypes = getStripeMissingRequirementTypes(resolvedStripeAccountData);
  const classes = classNames(rootClassName || css.root, className);

  return (
    <section className={classes} role="status" aria-live="polite">
      <div className={css.content}>
        <h2 className={css.title}>
          <FormattedMessage
            id="MissingStripeInfoBanner.title"
            defaultMessage="Complete your Stripe payout setup"
          />
        </h2>
        <p className={css.description}>
          <FormattedMessage
            id="MissingStripeInfoBanner.description"
            defaultMessage="Some payout information is missing from your Stripe account. Please complete your Stripe onboarding so you can receive payouts without delays."
          />
        </p>
        {missingRequirementTypes.length > 0 ? (
          <div className={css.requirementBlock}>
            <p className={css.requirementIntro}>
              <FormattedMessage
                id="MissingStripeInfoBanner.requirementIntro"
                defaultMessage="Stripe requires additional information before payouts can be enabled."
              />
            </p>
            <MissingRequirements requirementTypes={missingRequirementTypes} />
          </div>
        ) : null}
      </div>
    </section>
  );
};

MissingRequirements.propTypes = {
  requirementTypes: arrayOf(string),
};

MissingStripeInfoBanner.propTypes = {
  rootClassName: string,
  className: string,
  currentUser: object,
  stripeAccount: object,
  stripeAccountData: object,
  stripeAccountFetched: bool,
  stripeAccountFetchInProgress: bool,
  sellerContext: bool,
  stripeRequired: bool,
};

export default MissingStripeInfoBanner;
